import { ResearchTopic, Location, SavedTopic } from '@/types'
import { apiCache, cacheKeys, cachedApiCall } from './cache'
import { getCurrentUserId } from './getUserId'
import { API_BASE_URL } from './apiBaseUrl'

export { API_BASE_URL } from './apiBaseUrl'

// =============================================
// AUTHENTICATION HELPERS
// =============================================

export function getAuthHeader(): Record<string, string> {
  const token = localStorage.getItem('access_token')
  return token ? { 'Authorization': `Bearer ${token}` } : {}
}

export type SavedArticlesChangeAction = 'save' | 'unsave'
export type SavedArticlesChangeKind = 'research' | 'intelligence'

export interface SavedArticlesChangedDetail {
  action: SavedArticlesChangeAction
  kind: SavedArticlesChangeKind
  id: string
}

function dispatchSavedArticlesChanged(detail: SavedArticlesChangedDetail): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent('saved-articles-changed', { detail }))
}

// =============================================
// CORE DATA TYPES
// =============================================

export interface Research {
  id: number
  title: string
  summary?: string
  query: string
  model: string
  is_public: boolean
  created_at: string
  completed_at?: string
  user_id: number
  category?: string
  view_count?: number
  image_url?: string
  report_text?: string
  written_report?: string
  deep_research_report?: string
  intelligence_report_id?: string | null
  sources?: string[]
  results?: {
    report_text?: string
    summary?: string
    key_findings?: string[]
    sources?: string[]
  }
  author?: {
    name: string
    email: string
    avatar?: string
  }
  report_type?: string
  cig?: any
  sib?: any
  written?: any
  scenario?: any
  qa?: any
  plan?: any
  deep_research?: { text?: string; citations?: any[]; sources?: any[]; status?: string; provider?: string } | null
}

// =============================================
// SHARED LINK API TYPES
// =============================================

export type SharePermissionType = 'private' | 'link_only' | 'public'

export interface ShareInfo {
  share_id: string
  permission_type: SharePermissionType
  owner_name: string
  is_owner: boolean
}

export interface SharedResearchResponse {
  share_info: ShareInfo
  research: Research
}

export interface CreateShareLinkRequest {
  permission_type: SharePermissionType
  expires_in_days?: number
  invited_emails?: string[]
}

export interface UpdateSharePermissionsRequest {
  permission_type?: SharePermissionType
  add_emails?: string[]
  remove_emails?: string[]
}

export interface ShareLinkResponse {
  share_id: string
  url: string
  permission_type: SharePermissionType
  expires_at?: string | null
  view_count: number
  created_at?: string | null
}

export interface ShareAnalyticsView {
  viewed_at: string
  viewer_name: string
  [key: string]: unknown
}

export interface ShareAnalyticsResponse {
  total_shares: number
  total_views: number
  last_shared?: string | null
  recent_views: ShareAnalyticsView[]
}

// =============================================
// ARTICLE TOKEN API TYPES
// =============================================

export interface MintArticleTokenResponse {
  token: string
}

export interface ResolveArticleResponse {
  research_id: number
  title: string
  summary?: string
  key_findings: string[]
  sources: string[]
  category?: string
  image_url?: string
  author?: { name?: string; email?: string; avatar_url?: string }
}

// =============================================
// RESEARCH API TYPES
// =============================================

export interface ResearchSubmitRequest {
  topic_text: string
  topic_id?: string
  context?: string[]
  use_background?: boolean
  is_public?: boolean
  model?: 'chatgpt' | 'gemini' | 'claude'
}

export interface ResearchStatusResponse {
  task_id: string
  status: string
  created_at?: number
  completed_at?: number
  error_details?: string | null
  partial_output_items?: number | null
  progress?: number
  is_public?: boolean
  current_phase?: string  // Real phase from backend: PLAN, COLLECT, EXTRACT, etc.
}

export interface ResearchResultResponse {
  task_id: string
  status: string
  topic_text: string
  report_text?: string
  summary?: string
  key_findings?: string[]
  sources?: string[]
  web_searches?: any[]
  created_at?: number
  completed_at?: number
  error_details?: string
  is_partial?: boolean
  title?: string
  is_public?: boolean
}

// =============================================
// PODCAST API
// =============================================

export interface PodcastInfo {
  exists: boolean
  audio_url?: string
  transcript_only?: boolean
  output_file?: string
  podcast_type?: string
  status?: 'queued' | 'processing' | 'ready' | 'failed' | 'missing'
  duration_seconds?: number
  artwork_url?: string
  error_message?: string
  report_id?: string
  research_id?: number
}

export async function createPodcastForResearch(
  researchId: number,
  options: { podcast_type?: 'short' | 'long'; transcript_only?: boolean } = {}
): Promise<PodcastInfo> {
  const response = await fetch(`${API_BASE_URL}/research/${researchId}/podcast`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader()
    },
    body: JSON.stringify({
      podcast_type: options.podcast_type || 'short',
      transcript_only: options.transcript_only ?? false
    })
  })
  if (!response.ok) {
    const msg = await response.text().catch(() => '')
    throw new Error(`Failed to create podcast: ${response.status} ${msg}`)
  }
  const data = await response.json()
  // Convert relative audio_url to absolute if needed
  if (data.audio_url && data.audio_url.startsWith('/')) {
    data.audio_url = `${API_BASE_URL}${data.audio_url}`
  }
  return data
}

export async function createIntelligencePodcast(
  reportId: string
): Promise<PodcastInfo> {
  const response = await fetch(`${API_BASE_URL}/api/intelligence/report/${reportId}/podcast`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader()
    }
  })
  if (!response.ok) {
    const msg = await response.text().catch(() => '')
    throw new Error(`Failed to create intelligence podcast: ${response.status} ${msg}`)
  }
  const data = await response.json()
  if (data.audio_url && data.audio_url.startsWith('/')) {
    data.audio_url = `${API_BASE_URL}${data.audio_url}`
  }
  return data
}

export async function getPodcastInfo(
  researchId: number
): Promise<PodcastInfo> {
  const response = await fetch(`${API_BASE_URL}/research/${researchId}/podcast`, {
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader()
    }
  })
  if (!response.ok) {
    throw new Error(`Failed to fetch podcast info: ${response.status}`)
  }
  const data = await response.json()
  // Convert relative audio_url to absolute if needed
  if (data.audio_url && data.audio_url.startsWith('/')) {
    data.audio_url = `${API_BASE_URL}${data.audio_url}`
  }
  return data
}

export async function getIntelligencePodcastInfo(
  reportId: string
): Promise<PodcastInfo> {
  const response = await fetch(`${API_BASE_URL}/api/intelligence/report/${reportId}/podcast`, {
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader()
    }
  })
  if (!response.ok) {
    throw new Error(`Failed to fetch intelligence podcast info: ${response.status}`)
  }
  const data = await response.json()
  if (data.audio_url && data.audio_url.startsWith('/')) {
    data.audio_url = `${API_BASE_URL}${data.audio_url}`
  }
  return data
}

// =============================================
// RESEARCH API FUNCTIONS
// =============================================

export async function fetchPublicResearch(): Promise<any[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/research/public`, {
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      }
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data.results || data.research || []
  } catch (error) {
    console.error('Error fetching public research:', error)
    return []
  }
}

/**
 * Mint a short-lived public access token for a public research report.
 * Returns the token string. Throws if the research is not public or not found.
 */
export async function mintPublicToken(researchId: number): Promise<string> {
  const response = await fetch(
    `${API_BASE_URL}/api/articles/mint_public_token?research_id=${researchId}`,
    { headers: { 'Content-Type': 'application/json' } }
  )
  if (!response.ok) throw new Error('Failed to mint public token')
  const data = await response.json()
  return data.token as string
}

/**
 * Fetch the latest public explore cards without requiring authentication.
 * Used by the landing page public reports feed.
 */
export async function fetchPublicExploreCards(limit = 12): Promise<any[]> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/research/explore/cards?limit=${limit}`,
      { headers: { 'Content-Type': 'application/json' } }
    )
    if (!response.ok) return []
    const data = await response.json()
    return data.cards || []
  } catch {
    return []
  }
}

export async function fetchUserResearch(): Promise<any[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/research/my/optimized?limit=100&offset=0`, {
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      }
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data.results || data.research || []
  } catch (error) {
    console.error('Error fetching user research:', error)
    return []
  }
}

export async function fetchSavedResearch(options?: { bypassCache?: boolean }): Promise<any[]> {
  const userId = getCurrentUserId()
  const cacheKey = cacheKeys.savedResearch(userId)

  const fetchSaved = async () => {
    const response = await fetch(`${API_BASE_URL}/research/saved`, {
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      }
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data.saved || []
  }

  if (options?.bypassCache) {
    apiCache.invalidate(cacheKey)
    const freshData = await fetchSaved()
    apiCache.set(cacheKey, freshData, 5 * 60 * 1000)
    return freshData
  }

  return cachedApiCall(cacheKey, fetchSaved, 5 * 60 * 1000) // Cache for 5 minutes
}

export async function getResearchDetails(researchId: number): Promise<any> {
  const cacheKey = cacheKeys.researchDetail(researchId)
  
  return cachedApiCall(cacheKey, async () => {
    const response = await fetch(`${API_BASE_URL}/research/${researchId}`, {
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      }
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return await response.json()
  }, 10 * 60 * 1000).catch(error => {
    console.error('Error fetching research details:', error)
    return null
  })
}

// Fetch details and full report text, merge, and return
export async function getResearchDetailsWithFullReport(researchId: number): Promise<any> {
  const details = await getResearchDetails(researchId)
  
  // If details already has report text, return it immediately
  if (details?.results?.report_text) {
    return details
  }

  // Otherwise fetch report text with caching
  const cacheKey = `report_text_${researchId}`
  
  try {
    const reportText = await cachedApiCall(cacheKey, async () => {
      const response = await fetch(`${API_BASE_URL}/research/${researchId}/report`, {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        }
      })

      if (response.ok) {
        const data = await response.json()
        return data?.report_text || ''
      }
      return ''
    }, 30 * 60 * 1000) // Cache for 30 minutes

    if (reportText) {
      if (!details.results) details.results = {}
      details.results.report_text = reportText
      // Keep compatibility with components using `content`
      details.results.content = reportText
    }
  } catch (e) {
    // Non-fatal; UI can still show summary
    console.warn('Failed to fetch full report text', e)
  }
  return details
}

export async function saveResearchArticle(researchId: number): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/research/${researchId}/save`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      }
    })

    if (response.ok) {
      // Invalidate saved research cache (user-scoped)
      apiCache.invalidate(cacheKeys.savedResearch(getCurrentUserId()))
      dispatchSavedArticlesChanged({ action: 'save', kind: 'research', id: String(researchId) })
      console.log('Research saved successfully, dispatched saved articles event')
    }
    
    return response.ok
  } catch (error) {
    console.error('Error saving research article:', error)
    return false
  }
}

export async function unsaveResearchArticle(researchId: number): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/research/${researchId}/save`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      }
    })

    if (response.ok) {
      // Invalidate saved research cache (user-scoped)
      apiCache.invalidate(cacheKeys.savedResearch(getCurrentUserId()))
      dispatchSavedArticlesChanged({ action: 'unsave', kind: 'research', id: String(researchId) })
      console.log('Research unsaved successfully, dispatched saved articles event')
    }
    
    return response.ok
  } catch (error) {
    console.error('Error unsaving research article:', error)
    return false
  }
}

// Intelligence Report Save/Unsave
export async function saveIntelligenceReport(reportId: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/intelligence/report/${reportId}/save`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      }
    })

    if (response.ok) {
      apiCache.invalidate(cacheKeys.savedResearch(getCurrentUserId()))
      dispatchSavedArticlesChanged({ action: 'save', kind: 'intelligence', id: reportId })
      console.log('Intelligence report saved successfully')
    }
    
    return response.ok
  } catch (error) {
    console.error('Error saving intelligence report:', error)
    return false
  }
}

export async function unsaveIntelligenceReport(reportId: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/intelligence/report/${reportId}/save`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      }
    })

    if (response.ok) {
      apiCache.invalidate(cacheKeys.savedResearch(getCurrentUserId()))
      dispatchSavedArticlesChanged({ action: 'unsave', kind: 'intelligence', id: reportId })
      console.log('Intelligence report unsaved successfully')
    }
    
    return response.ok
  } catch (error) {
    console.error('Error unsaving intelligence report:', error)
    return false
  }
}

function resolveIntelligenceImageUrl(imageUrl?: string | null): string | null {
  if (!imageUrl) return null
  if (imageUrl.startsWith('http')) return imageUrl
  return `${API_BASE_URL}${imageUrl}`
}

export async function generateIntelligenceReportImage(reportId: string): Promise<string | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/intelligence/report/${reportId}/generate-image`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      }
    })

    if (response.ok) {
      const data = await response.json()
      return resolveIntelligenceImageUrl(data.image_url || `/api/intelligence/report/${reportId}/image?v=${Date.now()}`)
    }
    return null
  } catch (error) {
    console.error('Error generating intelligence report image:', error)
    return null
  }
}

export async function fetchIntelligenceReportImage(reportId: string): Promise<string | null> {
  try {
    return resolveIntelligenceImageUrl(`/api/intelligence/report/${reportId}/image`)
  } catch (error) {
    console.error('Error fetching intelligence report image:', error)
    return null
  }
}

export async function likeResearchArticle(researchId: number): Promise<{ liked: boolean; count: number }> {
  try {
    const response = await fetch(`${API_BASE_URL}/research/${researchId}/like`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      }
    })
    if (!response.ok) {
      throw new Error(`Failed to like research (${response.status})`)
    }
    return await response.json()
  } catch (error) {
    console.error('Error liking research article:', error)
    throw error
  }
}

export async function unlikeResearchArticle(researchId: number): Promise<{ liked: boolean; count: number }> {
  try {
    const response = await fetch(`${API_BASE_URL}/research/${researchId}/like`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      }
    })
    if (!response.ok) {
      throw new Error(`Failed to unlike research (${response.status})`)
    }
    return await response.json()
  } catch (error) {
    console.error('Error unliking research article:', error)
    throw error
  }
}

export async function recordResearchView(researchId: number, source: 'web' | 'app' = 'web'): Promise<void> {
  try {
    await fetch(`${API_BASE_URL}/research/${researchId}/view?source=${source}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
    })
  } catch {
    // View recording is analytics-only; never surface errors to the user
  }
}

export async function fetchResearchLikeSummary(researchId: number): Promise<{ liked: boolean; count: number }> {
  try {
    const response = await fetch(`${API_BASE_URL}/research/${researchId}/likes`, {
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      }
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch like summary (${response.status})`)
    }
    return await response.json()
  } catch (error) {
    console.error('Error fetching research like summary:', error)
    throw error
  }
}

export async function updateResearchPrivacy(researchId: number, isPublic: boolean): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/research/${researchId}/privacy`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      },
      body: JSON.stringify({ is_public: isPublic })
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return true
  } catch (error) {
    console.error('Error updating research privacy:', error)
    return false
  }
}

export async function submitAuthenticatedResearch(request: ResearchSubmitRequest): Promise<{ task_id: string }> {
  try {
    // Helper: fetch with timeout to prevent 2–3 minute hangs on failure paths
    const fetchWithTimeout = async (input: RequestInfo, init: RequestInit = {}, timeoutMs = 25000) => {
      const controller = new AbortController()
      const id = setTimeout(() => controller.abort(), timeoutMs)
      try {
        const res = await fetch(input, { ...init, signal: controller.signal })
        return res
      } finally {
        clearTimeout(id)
      }
    }

    const payload: ResearchSubmitRequest = {
      ...request,
    }
    const response = await fetchWithTimeout(`${API_BASE_URL}/research/submit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      // Try to get error details from response
      let errorMessage = `HTTP error! status: ${response.status}`
      try {
        const errorData = await response.json()
        if (errorData.detail) {
          // Check for specific validation errors
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail
          } else if (Array.isArray(errorData.detail)) {
            // Handle Pydantic validation errors
            const validationErrors = errorData.detail.map((err: any) => {
              if (err.loc && err.loc.includes('topic_text') && err.type === 'string_too_short') {
                return 'Your research query needs to be at least 10 characters long. Please provide more detail about what you want to research.'
              }
              return err.msg || 'Validation error'
            })
            errorMessage = validationErrors.join('. ')
          }
        }
      } catch (e) {
        // If we can't parse the error response, use the default message
      }
      throw new Error(errorMessage)
    }

    return await response.json()
  } catch (error) {
    console.error('Error submitting authenticated research:', error)
    throw error
  }
}

export async function submitResearch(request: ResearchSubmitRequest): Promise<{ task_id: string }> {
  try {
    const fetchWithTimeout = async (input: RequestInfo, init: RequestInit = {}, timeoutMs = 25000) => {
      const controller = new AbortController()
      const id = setTimeout(() => controller.abort(), timeoutMs)
      try {
        const res = await fetch(input, { ...init, signal: controller.signal })
        return res
      } finally {
        clearTimeout(id)
      }
    }

    const payload: ResearchSubmitRequest = {
      ...request,
    }
    const response = await fetchWithTimeout(`${API_BASE_URL}/research/submit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return await response.json()
  } catch (error) {
    console.error('Error submitting research:', error)
    throw error
  }
}

export async function getResearchStatus(taskId: string): Promise<ResearchStatusResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/research/status/${taskId}`)

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return await response.json()
  } catch (error) {
    console.error('Error getting research status:', error)
    throw error
  }
}

export async function getResearchResult(taskId: string): Promise<ResearchResultResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/research/result/${taskId}`)

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return await response.json()
  } catch (error) {
    console.error('Error getting research result:', error)
    throw error
  }
}

export async function submitQuickResearch(request: ResearchSubmitRequest): Promise<ResearchResultResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/research/quick`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return await response.json()
  } catch (error) {
    console.error('Error submitting quick research:', error)
    throw error
  }
}

export async function submitInstantResearch(request: ResearchSubmitRequest): Promise<ResearchResultResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/research/instant`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return await response.json()
  } catch (error) {
    console.error('Error submitting instant research:', error)
    throw error
  }
}

export async function checkApiHealth(): Promise<{ status: string; version: string }> {
  try {
    const response = await fetch(`${API_BASE_URL}/health`)

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return await response.json()
  } catch (error) {
    console.error('Error checking API health:', error)
    throw error
  }
}

// =============================================
// WEBSOCKET MANAGER
// =============================================

class WebSocketManager {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 3;
  private reconnectDelay = 2000;
  private subscribers: Map<string, Set<(result: any) => void>> = new Map();
  private isConnecting = false;
  private isDisconnecting = false;
  private connectionTimeout: NodeJS.Timeout | null = null;

  constructor(private baseUrl: string) {}

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }

      if (this.isConnecting || this.isDisconnecting) {
        const checkConnection = () => {
          if (this.ws?.readyState === WebSocket.OPEN) {
            resolve();
          } else if (!this.isConnecting && !this.isDisconnecting) {
            reject(new Error('Connection failed'));
          } else {
            setTimeout(checkConnection, 100);
          }
        };
        checkConnection();
        return;
      }

      this.isConnecting = true;
      
      // Retry health check with timeout to avoid hanging forever
      const healthCheckWithTimeout = fetch(this.baseUrl + '/health', { 
        signal: AbortSignal.timeout(3000) 
      })
        .then(response => {
          if (!response.ok) {
            throw new Error('Backend not available');
          }
          return response;
        })
        .catch(error => {
          // If health check fails, still try websocket connection (might be transient)
          console.warn('Health check failed, attempting WebSocket anyway:', error);
          return { ok: false };
        });
      
      healthCheckWithTimeout
        .then(() => this.createWebSocketConnection())
        .then(() => {
          resolve();
        })
        .catch(error => {
          this.isConnecting = false;
          console.warn('WebSocket connection failed:', error);
          reject(error);
        });
    });
  }

  private createWebSocketConnection(): Promise<void> {
    return new Promise((resolve, reject) => {
      const wsUrl = this.baseUrl.replace(/^http/, 'ws') + '/ws';
      
      try {
        this.ws = new WebSocket(wsUrl);

        this.connectionTimeout = setTimeout(() => {
          if (this.ws?.readyState === WebSocket.CONNECTING) {
            this.ws.close();
            this.isConnecting = false;
            reject(new Error('Connection timeout'));
          }
        }, 5000);

        this.ws.onopen = () => {
          // console.log('🔗 WebSocket connected');
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          
          if (this.connectionTimeout) {
            clearTimeout(this.connectionTimeout);
            this.connectionTimeout = null;
          }
          
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            this.handleMessage(data);
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };

        this.ws.onclose = (event) => {
          // console.log('🔌 WebSocket disconnected', event.code, event.reason);
          this.isConnecting = false;
          this.ws = null;
          
          if (this.connectionTimeout) {
            clearTimeout(this.connectionTimeout);
            this.connectionTimeout = null;
          }
          
          if (!this.isDisconnecting && event.code !== 1000 && event.code !== 1001) {
            setTimeout(() => {
              if (!this.isDisconnecting) {
                this.scheduleReconnect();
              }
            }, 1000);
          }
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          this.isConnecting = false;
          
          if (this.connectionTimeout) {
            clearTimeout(this.connectionTimeout);
            this.connectionTimeout = null;
          }
          
          reject(error);
        };
      } catch (error) {
        this.isConnecting = false;
        if (this.connectionTimeout) {
          clearTimeout(this.connectionTimeout);
          this.connectionTimeout = null;
        }
        reject(error);
      }
    });
  }

  private scheduleReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts && !this.isDisconnecting) {
      this.reconnectAttempts++;
      const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
      // console.log(`🔄 Scheduling WebSocket reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);
      
      setTimeout(() => {
        if (!this.isDisconnecting) {
          this.connect().catch(error => {
            console.error('WebSocket reconnect failed:', error);
          });
        }
      }, delay);
    } else {
      console.error('❌ Max WebSocket reconnect attempts reached or disconnecting');
    }
  }

  private handleMessage(data: any) {
    // console.log('📨 WebSocket message received:', data);

    if (data.type === 'task_completed') {
      const taskId = data.task_id;
      const result = data.result;
      
      const taskSubscribers = this.subscribers.get(taskId);
      if (taskSubscribers) {
        taskSubscribers.forEach(callback => {
          try {
            callback(result);
          } catch (error) {
            console.error('Error in WebSocket callback:', error);
          }
        });
      }
    } else if (data.type === 'subscription_confirmed') {
      // console.log(`✅ Subscribed to task: ${data.task_id}`);
    } else if (data.type === 'pong') {
      // console.log('🏓 WebSocket pong received');
    }
  }

  subscribeToTask(taskId: string, callback: (result: any) => void): () => void {
    if (!this.subscribers.has(taskId)) {
      this.subscribers.set(taskId, new Set());
    }
    this.subscribers.get(taskId)!.add(callback);

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({
        type: 'subscribe',
        task_id: taskId
      }));
    } else {
      this.connect().then(() => {
        if (this.ws?.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({
            type: 'subscribe',
            task_id: taskId
          }));
        }
      }).catch(error => {
        console.error('Failed to connect for task subscription:', error);
      });
    }

    return () => {
      const taskSubscribers = this.subscribers.get(taskId);
      if (taskSubscribers) {
        taskSubscribers.delete(callback);
        if (taskSubscribers.size === 0) {
          this.subscribers.delete(taskId);
        }
      }
    };
  }

  ping() {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'ping' }));
    }
  }

  disconnect() {
    this.isDisconnecting = true;
    
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }
    
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    
    this.subscribers.clear();
    this.reconnectAttempts = 0;
    this.isConnecting = false;
    this.isDisconnecting = false;
  }
}

// Global WebSocket manager instance
let wsManager: WebSocketManager | null = null;

export function getWebSocketManager(): WebSocketManager {
  if (!wsManager) {
    wsManager = new WebSocketManager(API_BASE_URL);
  }
  return wsManager;
}

// =============================================
// ENHANCED API CLIENT
// =============================================

class EnhancedApiClient {
  private wsManager: WebSocketManager;

  constructor() {
    this.wsManager = getWebSocketManager();
  }

  async submitResearch(request: ResearchSubmitRequest): Promise<{ task_id: string }> {
    const fetchWithTimeout = async (input: RequestInfo, init: RequestInit = {}, timeoutMs = 25000) => {
      const controller = new AbortController();
      const id = setTimeout(() => controller.abort(), timeoutMs);
      try {
        const res = await fetch(input, { ...init, signal: controller.signal });
        return res;
      } finally {
        clearTimeout(id);
      }
    };
    const payload: ResearchSubmitRequest = {
      ...request,
    };
    const response = await fetchWithTimeout(`${API_BASE_URL}/research/submit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    try {
      await this.wsManager.connect();
    } catch (error) {
      console.warn('WebSocket connection failed, falling back to polling:', error);
    }

    return result;
  }

  async getResearchStatus(taskId: string): Promise<ResearchStatusResponse> {
    return getResearchStatus(taskId);
  }

  async getResearchResult(taskId: string): Promise<ResearchResultResponse> {
    return getResearchResult(taskId);
  }

  async quickResearch(request: ResearchSubmitRequest): Promise<ResearchResultResponse> {
    const response = await fetch(`${API_BASE_URL}/research/quick`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  async instantResearch(request: ResearchSubmitRequest): Promise<ResearchResultResponse> {
    const response = await fetch(`${API_BASE_URL}/research/instant`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  subscribeToTaskCompletion(taskId: string, callback: (result: ResearchResultResponse) => void): () => void {
    return this.wsManager.subscribeToTask(taskId, callback);
  }

  async connectWebSocket(): Promise<void> {
    return this.wsManager.connect();
  }

  disconnectWebSocket(): void {
    this.wsManager.disconnect();
  }
}

// Export enhanced API client
export const apiClient = new EnhancedApiClient();

// =============================================
// COMPATIBILITY FUNCTIONS (for existing frontend code)
// =============================================

// These functions provide compatibility with existing frontend code
// while transitioning to real data

export async function fetchTopics(location: string = 'worldwide'): Promise<ResearchTopic[]> {
  // Fetch real public research and convert to ResearchTopic format
  const research = await fetchPublicResearch();
  
  return research.map(convertResearchToTopic);
}

export async function fetchTopicById(id: string): Promise<ResearchTopic | null> {
  // Try to find in public research first
  const research = await fetchPublicResearch();
  const found = research.find(r => r.id.toString() === id);
  
  if (found) {
    return convertResearchToTopic(found);
  }
  
  return null;
}

export async function fetchUserPublicResearch(userEmail: string): Promise<{ user: any; research: any[] }> {
  try {
    const response = await fetch(`${API_BASE_URL}/research/user/${encodeURIComponent(userEmail)}`, {
      headers: {
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('User not found')
      }
      throw new Error('Failed to fetch user research')
    }

    return await response.json()
  } catch (error) {
    console.error('Failed to fetch user public research:', error)
    throw error
  }
}

// Generate compelling titles from research content
function generateCompellingTitle(research: any): string {
  const query = research.query || research.title || '';
  const summary = research.results?.summary || research.summary || '';
  
  // Never use "Executive Summary" as a title
  if (research.title && research.title !== research.query && research.title.length > 10 && 
      !research.title.toLowerCase().includes('executive summary')) {
    return research.title;
  }
  
  // If the query itself is "Executive Summary", generate a better title
  if (query.toLowerCase() === 'executive summary' || query.toLowerCase().includes('executive summary')) {
    // Try to extract a better title from the summary or content
    if (summary) {
      const firstSentence = summary.split('.')[0];
      if (firstSentence && firstSentence.length > 20 && firstSentence.length < 100) {
        return `Research Report: ${firstSentence}`;
      }
    }
    return 'Comprehensive Research Analysis';
  }
  
  // Extract key insights from summary to create compelling titles
  if (summary) {
    const summaryLower = summary.toLowerCase();
    
    // Look for compelling insights in the summary
    if (summaryLower.includes('breakthrough') || summaryLower.includes('revolutionary')) {
      return `Revolutionary Breakthrough: ${query}`;
    }
    if (summaryLower.includes('crisis') || summaryLower.includes('urgent')) {
      return `Critical Analysis: ${query}`;
    }
    if (summaryLower.includes('future') || summaryLower.includes('predict')) {
      return `Future Insights: ${query}`;
    }
    if (summaryLower.includes('impact') || summaryLower.includes('effect')) {
      return `Impact Analysis: ${query}`;
    }
    if (summaryLower.includes('trend') || summaryLower.includes('pattern')) {
      return `Emerging Trends: ${query}`;
    }
    if (summaryLower.includes('discover') || summaryLower.includes('finding')) {
      return `Key Discoveries: ${query}`;
    }
  }
  
  // Category-based compelling prefixes
  const category = getCategoryForTitle(query);
  const categoryPrefixes: Record<string, string[]> = {
    technology: ['Tech Deep Dive:', 'Innovation Report:', 'Digital Transformation:', 'Tech Analysis:'],
    science: ['Scientific Breakthrough:', 'Research Findings:', 'Scientific Analysis:', 'Discovery Report:'],
    finance: ['Market Analysis:', 'Financial Insights:', 'Economic Report:', 'Investment Analysis:'],
    business: ['Business Intelligence:', 'Market Research:', 'Industry Analysis:', 'Strategic Insights:'],
    history: ['Historical Analysis:', 'Timeline Study:', 'Historical Insights:', 'Past & Present:'],
    sports: ['Sports Analysis:', 'Performance Study:', 'Athletic Insights:', 'Sports Report:'],
    general: ['Research Report:', 'Deep Analysis:', 'Comprehensive Study:', 'Expert Insights:']
  };
  
  const prefixes = categoryPrefixes[category] || categoryPrefixes.general;
  const randomPrefix = prefixes[Math.abs(query.split('').reduce((a: number, b: string) => (a << 5) - a + b.charCodeAt(0), 0)) % prefixes.length];
  
  // Clean up the query for the title
  const cleanQuery = query
    .replace(/^(research|study|analysis|report)\s+/i, '')
    .replace(/\s+(research|study|analysis|report)$/i, '')
    .trim();
  
  return `${randomPrefix} ${cleanQuery}`;
}

// Generate a concise blurb from research content (2-3 sentences max)
function generateConciseBlurb(research: any): string {
  const summary = research.results?.summary || research.summary || '';
  const query = research.query || research.title || '';
  
  // Remove any "Executive Summary" text
  let cleanSummary = summary
    .replace(/^\*\*Executive Summary\*\*:?\s*/i, '')
    .replace(/^Executive Summary:?\s*/i, '')
    .trim();
  
  if (cleanSummary) {
    // Extract first 2-3 sentences
    const sentences = cleanSummary.match(/[^.!?]+[.!?]+/g) || [];
    const blurb = sentences.slice(0, 2).join(' ').trim();
    
    // Ensure it's not too long (max 200 chars)
    if (blurb.length > 200) {
      return blurb.substring(0, 197) + '...';
    }
    return blurb;
  }
  
  // Fallback to query-based description
  return `Research exploring ${query.toLowerCase()}`;
}

// Generate intelligent single-word tags based on content
function generateIntelligentTags(research: any): string[] {
  const title = research.title || research.query || '';
  const summary = research.results?.summary || research.summary || '';
  const content = `${title} ${summary}`.toLowerCase();
  
  // Define tag mappings based on keywords
  const tagMappings: Record<string, string[]> = {
    'AI': ['artificial intelligence', 'machine learning', 'neural', 'algorithm', 'deepmind', 'alphafold'],
    'Tech': ['technology', 'software', 'hardware', 'computer', 'digital', 'innovation'],
    'Health': ['health', 'medical', 'medicine', 'disease', 'treatment', 'hospital', 'healthcare'],
    'Environment': ['climate', 'environment', 'sustainability', 'carbon', 'renewable', 'pollution', 'green'],
    'Finance': ['finance', 'money', 'economy', 'market', 'investment', 'crypto', 'payment', 'returns', 'economic', 'financial'],
    'Science': ['science', 'research', 'study', 'experiment', 'discovery', 'protein', 'biology', 'advancements', 'acoustic levitation'],
    'History': ['history', 'historical', 'ancient', 'civilization', 'bronze age', 'collapse', 'fall', 'empire', 'war', 'battle', 'conquest', 'dynasty', 'medieval', 'century', 'dutch', 'amsterdam', 'manhattan', 'colonial', 'stink', '1858', 'london', 'thames', 'sewers', 'new amsterdam', 'great stink', 'bronze age collapse', 'late bronze age', 'kush empire', 'rise and fall', 'muhammad', 'prophet', 'pharaoh', 'benin', 'imperialism'],
    'Politics': ['politics', 'government', 'policy', 'regulation', 'law', 'democracy', 'election'],
    'Business': ['business', 'company', 'startup', 'entrepreneur', 'market', 'product', 'corporate', 'uber'],
    'Sports': ['steph curry', 'curry shooting', 'lebron', 'basketball', 'football', 'soccer', 'tennis', 'golf', 'baseball', 'hockey', 'volleyball', 'swimming', 'nba', 'nfl', 'fifa', 'championship', 'playoff', 'tournament', 'stadium', 'arena', 'touchdown', 'dunk', 'pitcher', 'quarterback', 'striker', 'defender', 'midfielder', 'olympic', 'athletic performance', 'sport mastery', 'three-point shooting'],
    'Entertainment': ['entertainment', 'movie', 'film', 'television', 'tv', 'netflix', 'streaming', 'gaming', 'video game', 'music', 'concert'],
    'Education': ['education', 'learning', 'school', 'university', 'student', 'teaching'],
    'Space': ['space', 'nasa', 'mars', 'astronomy', 'satellite', 'rocket'],
    'Energy': ['energy', 'power', 'electricity', 'solar', 'nuclear', 'renewable'],
    'Security': ['security', 'cyber', 'privacy', 'encryption', 'threat', 'defense'],
    'Culture': ['culture', 'art', 'literature', 'society', 'tradition'],
    'Infrastructure': ['infrastructure', 'sanitation', 'urban planning', 'public health'],
    'Biography': ['life', 'biography', 'person', 'individual', 'story', 'profile'],
    'Transportation': ['transportation', 'transport', 'car', 'vehicle', 'train', 'plane', 'aviation', 'automotive'],
    'Food': ['food', 'cooking', 'restaurant', 'cuisine', 'recipe', 'chef', 'dining'],
    'Travel': ['travel', 'tourism', 'vacation', 'destination', 'hotel', 'trip'],
    'Law': ['law', 'legal', 'court', 'judge', 'lawyer', 'attorney', 'litigation'],
    'Real Estate': ['real estate', 'property', 'housing', 'construction', 'building', 'architecture'],
    'Fashion': ['fashion', 'clothing', 'style', 'design', 'apparel', 'textile'],
    'Military': ['military', 'defense', 'army', 'navy', 'air force', 'war', 'combat']
  };
  
  const tags: string[] = [];
  
  // Check each tag mapping
  for (const [tag, keywords] of Object.entries(tagMappings)) {
    if (keywords.some(keyword => content.includes(keyword))) {
      tags.push(tag);
      if (tags.length >= 3) break; // Limit to 3 tags
    }
  }
  
  // If no tags found, use category-based fallback
  if (tags.length === 0) {
    const category = getCategoryForTitle(title);
    const categoryTagMap: Record<string, string> = {
      'technology': 'Tech',
      'science': 'Science',
      'finance': 'Finance',
      'business': 'Business',
      'history': 'History',
      'sports': 'Sports',
      'infrastructure': 'Infrastructure',
      'culture': 'Culture',
      'entertainment': 'Entertainment',
      'transportation': 'Transportation',
      'food': 'Food',
      'travel': 'Travel',
      'law': 'Law',
      'real estate': 'Real Estate',
      'fashion': 'Fashion',
      'military': 'Military',
      'general': 'General'
    };
    tags.push(categoryTagMap[category] || 'General');
  }
  
  return tags;
}

// Convert research from database format to ResearchTopic format
function convertResearchToTopic(research: any): ResearchTopic {
  const generatedTags = generateIntelligentTags(research);
  
  // Fix image URL - prepend API base URL if it's a relative path
  let imageUrl = research.image_url;
  if (imageUrl && imageUrl.startsWith('/')) {
    imageUrl = `${API_BASE_URL}${imageUrl}`;
  }
  
  // Use a placeholder gradient if no image
  if (!imageUrl) {
    // Generate a deterministic gradient based on research ID
    const gradientColors = [
      ['#667eea', '#764ba2'],
      ['#f093fb', '#f5576c'],
      ['#4facfe', '#00f2fe'],
      ['#43e97b', '#38f9d7'],
      ['#fa709a', '#fee140'],
      ['#30cfd0', '#330867'],
      ['#a8edea', '#fed6e3'],
      ['#ff9a9e', '#fecfef']
    ];
    const gradientIndex = parseInt(research.id) % gradientColors.length;
    const [color1, color2] = gradientColors[gradientIndex];
    
    // Create a simple SVG gradient placeholder
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="200">
      <defs>
        <linearGradient id="g" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="${color1}"/>
          <stop offset="100%" stop-color="${color2}"/>
        </linearGradient>
      </defs>
      <rect width="400" height="200" fill="url(#g)"/>
    </svg>`;
    
    imageUrl = `data:image/svg+xml,${encodeURIComponent(svg)}`;
  }
  
  return {
    id: research.id.toString(),
    title: generateCompellingTitle(research),
    description: generateConciseBlurb(research),
    summary: research.results?.summary || research.summary || 'No summary available',
    category: research.category || generatedTags[0] || 'General', // Use DB category first, then generated tag
    trending_percentage: Math.floor(Math.random() * 100), // TODO: Calculate real trending
    engagement_count: research.view_count || 0,
    sources: research.results?.sources || research.sources || [],
    created_at: research.created_at,
    image_url: imageUrl,
    tags: generatedTags,
    key_findings: research.results?.key_findings || research.key_findings || [],
    report_text: research.results?.report_text || research.written_report || research.deep_research_report || research.report_text || '',
    query: research.query // Add original query
  };
}

// Cache for AI categorization results
const categoryCache = new Map<string, { category: string; timestamp: number }>();
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

// Helper function to get AI-powered category with caching
export async function getAICategory(title: string, summary: string = '', query: string = ''): Promise<string> {
  // Create cache key
  const cacheKey = `${title}|${summary}|${query}`.toLowerCase();
  
  // Check cache
  const cached = categoryCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.category;
  }
  
  try {
    const response = await fetch(`${API_BASE_URL}/categorize`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      },
      body: JSON.stringify({
        title: title || '',
        summary: summary || '',
        query: query || ''
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const result = await response.json();
    
    // Cache the result
    categoryCache.set(cacheKey, {
      category: result.category,
      timestamp: Date.now()
    });
    
    // Clean old cache entries if too many
    if (categoryCache.size > 100) {
      const entries = Array.from(categoryCache.entries());
      entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      entries.slice(0, 20).forEach(([key]) => categoryCache.delete(key));
    }
    
    return result.category;
  } catch (error) {
    console.error('AI categorization failed, using fallback:', error);
    // Fallback to keyword-based categorization
    return getCategoryForTitle(title);
  }
}

// Helper function to categorize research based on title
export function getCategoryForTitle(title: string): string {
  const titleLower = title.toLowerCase();
  
  // History keywords (expanded)
  if (titleLower.includes('history') || titleLower.includes('historical') || titleLower.includes('ancient') || 
      titleLower.includes('war') || titleLower.includes('empire') || titleLower.includes('fall') || 
      titleLower.includes('collapse') || titleLower.includes('conquest') || titleLower.includes('dynasty') || 
      titleLower.includes('medieval') || titleLower.includes('century') || titleLower.includes('dutch') || 
      titleLower.includes('amsterdam') || titleLower.includes('manhattan') || titleLower.includes('colonial') ||
      titleLower.includes('bronze age') || titleLower.includes('civilization') || titleLower.includes('1858') ||
      titleLower.includes('great stink') || titleLower.includes('london') || titleLower.includes('thames') ||
      titleLower.includes('kush') || titleLower.includes('prophet') || titleLower.includes('muhammad')) {
    return 'history';
  } 
  // Technology keywords
  else if (titleLower.includes('ai') || titleLower.includes('technology') || titleLower.includes('algorithm') || 
           titleLower.includes('software') || titleLower.includes('digital') || titleLower.includes('computer')) {
    return 'technology';
  } 
  // Finance keywords
  else if (titleLower.includes('economy') || titleLower.includes('finance') || titleLower.includes('money') || 
           titleLower.includes('investment') || titleLower.includes('market') || titleLower.includes('returns') ||
           titleLower.includes('payment') || titleLower.includes('uber') || titleLower.includes('wallet')) {
    return 'finance';
  } 
  // Sports keywords
  else if (titleLower.includes('sport') || titleLower.includes('basketball') || titleLower.includes('game') || 
           titleLower.includes('curry') || titleLower.includes('shooting') || titleLower.includes('athlete')) {
    return 'sports';
  } 
  // Science keywords
  else if (titleLower.includes('science') || titleLower.includes('research') || titleLower.includes('study') || 
           titleLower.includes('experiment') || titleLower.includes('discovery') || titleLower.includes('acoustic') ||
           titleLower.includes('levitation') || titleLower.includes('advancements')) {
    return 'science';
  } 
  // Infrastructure keywords
  else if (titleLower.includes('sewers') || titleLower.includes('sanitation') || titleLower.includes('infrastructure') ||
           titleLower.includes('urban planning') || titleLower.includes('public health')) {
    return 'infrastructure';
  }
  // Culture keywords
  else if (titleLower.includes('culture') || titleLower.includes('art') || titleLower.includes('music') || 
           titleLower.includes('literature') || titleLower.includes('society') || titleLower.includes('tradition')) {
    return 'culture';
  }
  else {
    return 'General';
  }
}



// Saved topics functionality using real backend
export async function saveTopicForLater(topicId: string): Promise<boolean> {
  return await saveResearchArticle(parseInt(topicId));
}

export async function getSavedTopics(): Promise<SavedTopic[]> {
  const saved = await fetchSavedResearch();
  
  return saved.map(savedResearch => ({
    id: savedResearch.id.toString(),
    topic: convertResearchToTopic(savedResearch),
    saved_at: savedResearch.saved_at || savedResearch.created_at
  }));
}

export async function removeSavedTopic(savedTopicId: string): Promise<boolean> {
  return await unsaveResearchArticle(parseInt(savedTopicId));
}

export async function searchSavedTopics(query: string): Promise<SavedTopic[]> {
  const saved = await getSavedTopics();
  
  if (!query.trim()) return saved;
  
  return saved.filter((savedTopic: SavedTopic) => 
    savedTopic.topic.title.toLowerCase().includes(query.toLowerCase()) ||
    savedTopic.topic.description.toLowerCase().includes(query.toLowerCase()) ||
    savedTopic.topic.tags.some(tag => tag.toLowerCase().includes(query.toLowerCase()))
  );
}

// Export locations for compatibility (these can be real or mock as needed)
export const LOCATIONS: Location[] = [
  { id: 'worldwide', name: 'Worldwide', code: 'WW', flag: '' },
  { id: 'us', name: 'United States & Canada', code: 'US&C', flag: '' },
  { id: 'apac', name: 'Asia Pacific', code: 'APAC', flag: '' },
  { id: 'emea', name: 'Europe, Middle East & Africa', code: 'EMEA', flag: '' },
  { id: 'latam', name: 'Latin America', code: 'LatAm', flag: '' },
  { id: 'sf', name: 'San Francisco, California', code: 'SF', flag: '' },
];

// =============================================
// SHARED LINK API
// =============================================

async function readApiErrorDetail(response: Response, fallback: string): Promise<string> {
  const err = await response.json().catch(() => ({}))
  return typeof err?.detail === 'string' ? err.detail : fallback
}

export async function createShareLink(
  researchId: number,
  request: CreateShareLinkRequest
): Promise<ShareLinkResponse> {
  const response = await fetch(`${API_BASE_URL}/research/${researchId}/share`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
    body: JSON.stringify(request),
  })

  if (!response.ok) {
    throw new Error(await readApiErrorDetail(response, 'Failed to create share link'))
  }

  return response.json()
}

export async function updateSharePermissions(
  shareId: string,
  request: UpdateSharePermissionsRequest
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/share/${shareId}/permissions`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
    body: JSON.stringify(request),
  })

  if (!response.ok) {
    throw new Error(await readApiErrorDetail(response, 'Failed to update share permissions'))
  }
}

export async function revokeShareLink(shareId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/share/${shareId}`, {
    method: 'DELETE',
    headers: {
      ...getAuthHeader(),
    },
  })

  if (!response.ok) {
    throw new Error(await readApiErrorDetail(response, 'Failed to revoke share link'))
  }
}

export async function getShareAnalytics(researchId: number): Promise<ShareAnalyticsResponse> {
  const response = await fetch(`${API_BASE_URL}/research/${researchId}/share-analytics`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
  })

  if (!response.ok) {
    throw new Error(await readApiErrorDetail(response, 'Failed to get share analytics'))
  }

  return response.json()
}

export async function getSharedContent(shareId: string): Promise<SharedResearchResponse> {
  const response = await fetch(`${API_BASE_URL}/s/${shareId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json, text/plain, */*',
      'X-Requested-With': 'XMLHttpRequest',
      ...getAuthHeader()
    }
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.detail || 'Failed to get shared content')
  }

  return response.json()
}

// =============================================
// ARTICLE TOKEN API
// =============================================

export async function mintArticleToken(researchId: number): Promise<string> {
  const response = await fetch(`${API_BASE_URL}/api/articles/mint_token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
    body: JSON.stringify({ research_id: researchId, scope: 'owner' }),
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.detail || 'Failed to mint article token')
  }
  const data: MintArticleTokenResponse = await response.json()
  return data.token
}

/**
 * Decode an article token payload on the client **without** verifying the signature.
 * This is safe because we only use it to discover the research ID for additional
 * permission-checked API calls (the backend still enforces access control).
 */
export function decodeArticleTokenPayload(token: string): any | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const payloadB64 = parts[1]
    // Convert base64url → base64
    const normalized = payloadB64
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(payloadB64.length + (4 - (payloadB64.length % 4 || 4)), '=')
    const jsonStr = atob(normalized)
    return JSON.parse(jsonStr)
  } catch (e) {
    console.warn('Failed to decode article token payload', e)
    return null
  }
}

/**
 * Check if an article token has "public" scope (can be viewed without authentication).
 */
export function isPublicArticleToken(token: string | null): boolean {
  if (!token) return false
  const payload = decodeArticleTokenPayload(token)
  return payload?.scope === 'public'
}

/**
 * Try to resolve a **public** article token by falling back to the normal
 * research detail endpoints when `/api/articles/resolve` fails.
 */
async function tryResolvePublicArticleFallback(token: string): Promise<ResolveArticleResponse | null> {
  const payload = decodeArticleTokenPayload(token)
  if (!payload || payload.scope !== 'public' || !payload.rid) {
    return null
  }

  const researchId = Number(payload.rid)
  if (!Number.isFinite(researchId)) return null

  try {
    const details = await getResearchDetailsWithFullReport(researchId)
    if (!details) return null

    const results = (details as any).results || {}
    const author =
      (details as any).author ||
      ((details as any).author_name
        ? {
            name: (details as any).author_name,
            email: (details as any).author_email,
            avatar_url: (details as any).author_avatar,
          }
        : undefined)

    const fallback: ResolveArticleResponse = {
      research_id: details.id,
      title: details.title || details.query || 'Research',
      summary: results.summary || details.summary,
      key_findings: results.key_findings || details.key_findings || [],
      sources: results.sources || details.sources || [],
      category: details.category,
      image_url: details.image_url,
      author,
    }
    return fallback
  } catch (e) {
    console.warn('Public token fallback failed', e)
    return null
  }
}

export async function resolveArticleByToken(token: string): Promise<ResolveArticleResponse> {
  const url = new URL(`${API_BASE_URL}/api/articles/resolve`)
  url.searchParams.set('token', token)
  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
  })

  if (response.ok) {
    return response.json()
  }

  // On auth/expiry style failures, attempt a safe public fallback
  if ([401, 403, 404].includes(response.status)) {
    const fallback = await tryResolvePublicArticleFallback(token)
    if (fallback) {
      return fallback
    }
  }

  const err = await response.json().catch(() => ({}))
  throw new Error(err.detail || 'Failed to resolve article token')
}

export async function mintPublicArticleToken(researchId: number): Promise<string> {
  const url = new URL(`${API_BASE_URL}/api/articles/mint_public_token`)
  url.searchParams.set('research_id', String(researchId))
  const response = await fetch(url.toString(), { method: 'GET', headers: { 'Content-Type': 'application/json' } })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.detail || 'Failed to mint public token')
  }
  const data: MintArticleTokenResponse = await response.json()
  return data.token
}

export async function updateUserProfile(firstName: string, lastName: string, location: string | null): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/auth/profile`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader()
    },
    body: JSON.stringify({
      first_name: firstName,
      last_name: lastName,
      location: location || null
    })
  })
  
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.detail || 'Failed to update profile')
  }
  
  return response.json()
}

export async function checkUsernameAvailability(username: string): Promise<{ available: boolean }> {
  const response = await fetch(`${API_BASE_URL}/user/username-available?u=${encodeURIComponent(username)}`, {
    method: 'GET',
    headers: {
      ...getAuthHeader()
    }
  })
  
  if (!response.ok) {
    throw new Error('Failed to check username availability')
  }
  
  return response.json()
}

export async function updateUserIdentity(username: string | null, bio: string | null): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/user/profile`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader()
    },
    body: JSON.stringify({
      username: username,
      bio: bio
    })
  })
  
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.detail || 'Failed to update profile')
  }
  
  return response.json()
}

export async function getUserProfile(): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/user/me`, {
    method: 'GET',
    headers: {
      ...getAuthHeader()
    }
  })
  
  if (!response.ok) {
    throw new Error('Failed to get user profile')
  }
  
  return response.json()
}

export async function getUserStats(): Promise<{ total: number; public: number; private: number; completed: number; in_progress: number; failed: number }> {
  const response = await fetch(`${API_BASE_URL}/user/stats`, {
    method: 'GET',
    headers: {
      ...getAuthHeader()
    }
  })
  if (!response.ok) {
    throw new Error('Failed to get user stats')
  }
  return response.json()
}

export async function triggerSessionRecovery(): Promise<{ success: boolean; recovered: number }> {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/scan/session-recovery`, {
      method: 'POST',
      headers: {
        ...getAuthHeader()
      }
    })
    
    if (!response.ok) {
      return { success: false, recovered: 0 }
    }
    
    return response.json()
  } catch (error) {
    console.error('Session recovery failed:', error)
    return { success: false, recovered: 0 }
  }
}

// =============================================
// NOTIFICATION PREFERENCES API
// =============================================

export interface NotificationPreferences {
  research_complete: boolean
  research_failed: boolean
  marketing: boolean
}

export async function getNotificationPreferences(): Promise<NotificationPreferences> {
  const response = await fetch(`${API_BASE_URL}/user/notification-preferences`, {
    method: 'GET',
    headers: {
      ...getAuthHeader()
    }
  })
  
  if (!response.ok) {
    throw new Error('Failed to get notification preferences')
  }
  
  const data = await response.json()
  return data.preferences
}

export async function updateNotificationPreferences(preferences: Partial<NotificationPreferences>): Promise<NotificationPreferences> {
  const response = await fetch(`${API_BASE_URL}/user/notification-preferences`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader()
    },
    body: JSON.stringify({ preferences })
  })
  
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.detail || 'Failed to update notification preferences')
  }
  
  const data = await response.json()
  return data.preferences
}

// =============================================
// SUPPORT REQUEST API
// =============================================

export interface SupportRequestResponse {
  success: boolean
  message: string
  request_id: number
}

export async function submitSupportRequest(subject: string, message: string): Promise<SupportRequestResponse> {
  const response = await fetch(`${API_BASE_URL}/support/request`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader()
    },
    body: JSON.stringify({ subject, message })
  })
  
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.detail || 'Failed to submit support request')
  }
  
  return response.json()
}
  